import { DropdownMenu as KobalteDropdownMenu } from "@kobalte/core/dropdown-menu"
import { NODES } from "@repo/fika-blog/src/nodes"
import type { IconName } from "@repo/fika-design-system/icon"
import { createMemo, For, type ParentProps } from "solid-js"
import {
  dropdownContentClass,
  dropdownItemLabelClass,
  dropdownItemRecipe,
} from "@/designSystem/components/dropdown-menu.css"
import { Icon } from "@/designSystem/components/icon"
import { useI18nContext } from "@/i18n/i18n-solid"

interface TransformTarget {
  value: string
  label: string
  iconName: IconName
}

type DefaultMenuProps = ParentProps<{
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  triggerClass: string
  triggerLabel: string
  onTriggerPointerDown: () => void
  onTriggerClick: (event: MouseEvent) => void
  onTriggerKeyDown: (event: KeyboardEvent) => void
  onTriggerDragStart: (event: DragEvent) => void
  onTriggerDragEnd: () => void
  onTransform: (value: string) => void
  onDelete: () => void
}>

export function DefaultMenu(props: DefaultMenuProps) {
  const { LL } = useI18nContext()

  const transformTargets = createMemo<TransformTarget[]>(() => [
    { value: "paragraph", label: LL().editor.text(), iconName: "type" },
    {
      value: "heading-1",
      label: LL().editor.heading1(),
      iconName: "heading-1",
    },
    {
      value: "heading-2",
      label: LL().editor.heading2(),
      iconName: "heading-2",
    },
    {
      value: "heading-3",
      label: LL().editor.heading3(),
      iconName: "heading-3",
    },
    {
      value: "blockquote",
      label: LL().editor.blockquote(),
      iconName: "quote",
    },
    {
      value: "callout",
      label: LL().editor.callout(),
      iconName: "messages-square",
    },
    {
      value: "codeBlock",
      label: LL().editor.code(),
      iconName: NODES.codeBlock.iconName,
    },
  ])

  return (
    <KobalteDropdownMenu open={props.isOpen} onOpenChange={props.onOpenChange}>
      <KobalteDropdownMenu.Trigger
        class={props.triggerClass}
        aria-label={props.triggerLabel}
        data-block-node-menu-trigger="true"
        draggable="true"
        onPointerDown={props.onTriggerPointerDown}
        onClick={props.onTriggerClick}
        onKeyDown={props.onTriggerKeyDown}
        onDragStart={props.onTriggerDragStart}
        onDragEnd={props.onTriggerDragEnd}
      >
        {props.children}
      </KobalteDropdownMenu.Trigger>
      <KobalteDropdownMenu.Portal>
        <KobalteDropdownMenu.Content class={dropdownContentClass}>
          <KobalteDropdownMenu.Sub>
            <KobalteDropdownMenu.SubTrigger
              class={dropdownItemRecipe({ hue: "sand" })}
            >
              <Icon name="type" size="18" />
              <span class={dropdownItemLabelClass}>
                {LL().editor.turnIntoLabel()}
              </span>
              <Icon name="chevron-right" size="18" />
            </KobalteDropdownMenu.SubTrigger>
            <KobalteDropdownMenu.Portal>
              <KobalteDropdownMenu.SubContent class={dropdownContentClass}>
                <For each={transformTargets()}>
                  {target => (
                    <KobalteDropdownMenu.Item
                      class={dropdownItemRecipe({ hue: "sand" })}
                      onSelect={() => props.onTransform(target.value)}
                    >
                      <Icon name={target.iconName} size="18" />
                      <span class={dropdownItemLabelClass}>{target.label}</span>
                    </KobalteDropdownMenu.Item>
                  )}
                </For>
              </KobalteDropdownMenu.SubContent>
            </KobalteDropdownMenu.Portal>
          </KobalteDropdownMenu.Sub>
          <KobalteDropdownMenu.Item
            class={dropdownItemRecipe({ hue: "rose" })}
            onSelect={props.onDelete}
          >
            <Icon name="trash-2" size="18" />
            <span class={dropdownItemLabelClass}>{LL().common.delete()}</span>
          </KobalteDropdownMenu.Item>
        </KobalteDropdownMenu.Content>
      </KobalteDropdownMenu.Portal>
    </KobalteDropdownMenu>
  )
}
